import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarCheck, RefreshCw, FileDown, FileSpreadsheet, Loader2, Warehouse, CalendarDays,
  TrendingUp, TrendingDown, PackageCheck, Truck, FileEdit, ShoppingCart,
  CheckCircle2, CreditCard, ClipboardEdit,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — Day Close Report.
 *
 * End-of-day dashboard covering receiving, dispatch, stock
 * adjustments, orders, payments, and manual ledger entries. Every
 * section can be toggled off via checkboxes, exported as a full
 * PDF, or exported as a deep-dive (single-section) PDF.
 */

const ACTIVITY_TYPES = [
  { key: 'receiving',     label: 'Receiving',          icon: PackageCheck, colour: 'emerald' },
  { key: 'dispatch',      label: 'Dispatch',           icon: Truck,        colour: 'rose'    },
  { key: 'adjustments',   label: 'Stock Adjustments',  icon: FileEdit,     colour: 'blue'    },
  { key: 'new_orders',    label: 'New Orders',         icon: ShoppingCart, colour: 'indigo'  },
  { key: 'fulfilled',     label: 'Orders Fulfilled',   icon: CheckCircle2, colour: 'teal'    },
  { key: 'payments',      label: 'Payments',           icon: CreditCard,   colour: 'amber'   },
  { key: 'manual_ledger', label: 'Manual Ledger',      icon: ClipboardEdit,colour: 'slate'   },
];

export default function AdminDayClose() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Feb 2026 — Time range filter (datetime-local). Defaults to
  // "today 00:00 → now" in the user's local time. The values stay in
  // local time inside the input (datetime-local can't represent a
  // timezone); we convert to UTC ISO when sending to the backend.
  const today = new Date().toISOString().slice(0, 10);
  const buildDefaultRange = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const startLocal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T00:00`;
    const endLocal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return { startLocal, endLocal };
  };
  const defaults = buildDefaultRange();
  const [startLocal, setStartLocal] = useState(defaults.startLocal);
  const [endLocal, setEndLocal] = useState(defaults.endLocal);

  // Convert the datetime-local value (interpreted as the user's
  // local timezone) into an ISO UTC string the backend accepts.
  const toUtcIso = (localValue) => {
    if (!localValue) return '';
    try { return new Date(localValue).toISOString(); } catch { return ''; }
  };

  const rangeValid = Boolean(startLocal && endLocal) && new Date(endLocal) > new Date(startLocal);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [selected, setSelected] = useState(() => new Set(ACTIVITY_TYPES.map((t) => t.key)));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  // Feb 2026 — Division / Category filter.
  const [filterOptions, setFilterOptions] = useState({ divisions: [], categories: [] });
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const filterActive = (selectedDivisions.length > 0) || (selectedCategories.length > 0);

  const load = useCallback(async () => {
    if (!rangeValid) {
      toast.error('End datetime must be after start datetime.');
      return;
    }
    setLoading(true);
    try {
      const params = {
        start: toUtcIso(startLocal),
        end: toUtcIso(endLocal),
        row_limit: 200,
      };
      if (warehouseId) params.warehouse_id = warehouseId;
      if (selected.size < ACTIVITY_TYPES.length) params.types = Array.from(selected).join(',');
      if (selectedDivisions.length) params.divisions = selectedDivisions.join(',');
      if (selectedCategories.length) params.categories = selectedCategories.join(',');
      const { data } = await axios.get(`${API}/admin/day-close/summary`, {
        headers: authHeaders, params,
      });
      setSummary(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load day summary');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, startLocal, endLocal, rangeValid, warehouseId, selected, selectedDivisions, selectedCategories]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/warehouses`, { headers: authHeaders });
        setWarehouses(data.warehouses || data || []);
      } catch { /* optional */ }
    })();
  }, [authHeaders]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/day-close/filter-options`,
          { headers: authHeaders },
        );
        setFilterOptions({
          divisions: data?.divisions || [],
          categories: data?.categories || [],
        });
      } catch { /* optional — the endpoint 403s for non-super users */ }
    })();
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const clearFilter = () => {
    setSelectedDivisions([]);
    setSelectedCategories([]);
  };

  const openPdf = ({ deepDive = null } = {}) => {
    if (!rangeValid) {
      toast.error('End datetime must be after start datetime.');
      return;
    }
    const params = new URLSearchParams();
    params.set('start', toUtcIso(startLocal));
    params.set('end', toUtcIso(endLocal));
    if (warehouseId) params.set('warehouse_id', warehouseId);
    if (deepDive) {
      params.set('types', deepDive);
      params.set('mode', 'deep_dive');
    } else if (selected.size < ACTIVITY_TYPES.length) {
      params.set('types', Array.from(selected).join(','));
    }
    if (selectedDivisions.length) params.set('divisions', selectedDivisions.join(','));
    if (selectedCategories.length) params.set('categories', selectedCategories.join(','));
    params.set('token', token);
    window.open(`${API}/admin/day-close/pdf?${params.toString()}`, '_blank', 'noopener');
  };

  // Feb 2026 — Excel export. Same params + auth as the PDF endpoint,
  // returns an .xlsx workbook (KPI sheet + one sheet per activity +
  // chronological appendix). We fetch as a blob so the file downloads
  // cleanly on every browser (window.open would leave the tab open
  // with a blank page on Safari).
  const downloadXlsx = async ({ deepDive = null } = {}) => {
    if (!rangeValid) {
      toast.error('End datetime must be after start datetime.');
      return;
    }
    const params = new URLSearchParams();
    params.set('start', toUtcIso(startLocal));
    params.set('end', toUtcIso(endLocal));
    if (warehouseId) params.set('warehouse_id', warehouseId);
    if (deepDive) {
      params.set('types', deepDive);
      params.set('mode', 'deep_dive');
    } else if (selected.size < ACTIVITY_TYPES.length) {
      params.set('types', Array.from(selected).join(','));
    }
    if (selectedDivisions.length) params.set('divisions', selectedDivisions.join(','));
    if (selectedCategories.length) params.set('categories', selectedCategories.join(','));
    try {
      const res = await axios.get(
        `${API}/admin/day-close/xlsx?${params.toString()}`,
        { headers: authHeaders, responseType: 'blob' },
      );
      const stamp = startLocal.replace(/[-:T]/g, '').slice(0, 12);
      const filename = `day-close-${stamp}${deepDive ? `-${deepDive}` : ''}.xlsx`;
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Excel export failed.');
    }
  };

  const kpis = summary?.kpis || {};
  const sections = summary?.sections || {};

  return (
    <AdminLayout>
      <div className="min-h-full bg-slate-50 py-6 px-4 sm:px-6 lg:px-8" data-testid="day-close-page">
        <div className="max-w-7xl mx-auto space-y-5">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <CalendarCheck className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Day Close Report</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  End-of-day summary of every warehouse activity — receiving, dispatch, adjustments, orders, payments.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60"
                data-testid="day-close-refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => openPdf()}
                disabled={loading || !summary}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-[#0078D4] rounded-lg hover:bg-[#0068B8] disabled:opacity-60"
                data-testid="day-close-pdf-full"
              >
                <FileDown className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => downloadXlsx()}
                disabled={loading || !summary}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                data-testid="day-close-xlsx-full"
                title="Download the same report as an Excel workbook (one sheet per activity + KPI summary)"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Download Excel
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Start (local)</label>
                <div className="mt-1 relative">
                  <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="datetime-local"
                    value={startLocal}
                    onChange={(e) => setStartLocal(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4]"
                    data-testid="day-close-start"
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">End (local)</label>
                <div className="mt-1 relative">
                  <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="datetime-local"
                    value={endLocal}
                    onChange={(e) => setEndLocal(e.target.value)}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4] ${
                      rangeValid ? 'border-slate-300' : 'border-rose-400'
                    }`}
                    data-testid="day-close-end"
                  />
                </div>
                {!rangeValid && (
                  <p className="text-[11px] text-rose-600 mt-0.5" data-testid="day-close-range-error">
                    End must be after start.
                  </p>
                )}
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Warehouse</label>
                <div className="mt-1 relative">
                  <Warehouse className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <select
                    value={warehouseId}
                    onChange={(e) => setWarehouseId(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4] bg-white"
                    data-testid="day-close-warehouse"
                  >
                    <option value="">All warehouses</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name || w.code || w.id}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Quick ranges</label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="day-close-quick-ranges">
                  <button
                    type="button"
                    onClick={() => {
                      const d = buildDefaultRange();
                      setStartLocal(d.startLocal); setEndLocal(d.endLocal);
                    }}
                    className="px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
                    data-testid="day-close-quick-today-now"
                  >Today → now</button>
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const pad = (n) => String(n).padStart(2, '0');
                      const base = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
                      setStartLocal(`${base}T00:00`);
                      setEndLocal(`${base}T23:59`);
                    }}
                    className="px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
                    data-testid="day-close-quick-full-day"
                  >Full day</button>
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const past = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                      const pad = (n) => String(n).padStart(2, '0');
                      const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                      setStartLocal(toLocal(past));
                      setEndLocal(toLocal(now));
                    }}
                    className="px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
                    data-testid="day-close-quick-24h"
                  >Last 24 h</button>
                </div>
              </div>
              <div className="md:col-span-12">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Activity types (filter)</label>
                <div className="mt-1 flex flex-wrap gap-2" data-testid="day-close-type-filters">
                  {ACTIVITY_TYPES.map((t) => (
                    <label
                      key={t.key}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer border transition ${
                        selected.has(t.key)
                          ? 'bg-[#0078D4] text-white border-[#0078D4]'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`day-close-type-${t.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(t.key)}
                        onChange={() => toggle(t.key)}
                        className="hidden"
                      />
                      <t.icon className="w-3.5 h-3.5" />
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Feb 2026 — Product taxonomy filter. Narrows every
                  product-scoped section (receiving, dispatch, adjustments,
                  new orders, fulfilled, manual ledger) to the selected
                  divisions / categories. Payments have no product
                  context — the section shows a "hidden by scope" note. */}
              <div className="md:col-span-12" data-testid="day-close-taxonomy-filter">
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Divisions / Categories (filter)
                  </label>
                  <span
                    className={
                      filterActive
                        ? "text-[10px] font-bold uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
                        : "text-[10px] uppercase text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded"
                    }
                    data-testid="day-close-taxonomy-status"
                  >
                    {filterActive
                      ? (summary?.product_filter?.matched_product_count != null
                          ? `Scoped · ${summary.product_filter.matched_product_count} SKU(s)`
                          : 'Scoped')
                      : 'All SKUs'}
                  </span>
                  {filterActive && (
                    <button
                      type="button"
                      onClick={clearFilter}
                      className="ml-auto text-[11px] text-slate-600 hover:text-slate-900 underline"
                      data-testid="day-close-taxonomy-clear"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 mb-0.5">
                      Divisions
                    </div>
                    <select
                      multiple
                      value={selectedDivisions}
                      onChange={(e) =>
                        setSelectedDivisions(
                          Array.from(e.target.selectedOptions).map((o) => o.value),
                        )
                      }
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1 h-20 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      data-testid="day-close-filter-divisions"
                    >
                      {filterOptions.divisions.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 mb-0.5">
                      Categories
                    </div>
                    <select
                      multiple
                      value={selectedCategories}
                      onChange={(e) =>
                        setSelectedCategories(
                          Array.from(e.target.selectedOptions).map((o) => o.value),
                        )
                      }
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1 h-20 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      data-testid="day-close-filter-categories"
                    >
                      {filterOptions.categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Ctrl/⌘-click to select multiple. Leaving both empty runs against every SKU. Payments are hidden when a scope is active — they carry no product context.
                </div>
              </div>
            </div>
          </div>

          {/* KPI grid */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="day-close-kpis">
              <KPI icon={TrendingUp}   label="Receiving qty"   value={kpis.receiving_qty}   tone="emerald" />
              <KPI icon={TrendingDown} label="Dispatch qty"    value={kpis.dispatch_qty}    tone="rose"    />
              <KPI icon={FileEdit}     label="Adj IN / OUT"    value={`+${kpis.adjustments_in || 0} / −${kpis.adjustments_out || 0}`} tone="blue" />
              <KPI icon={ShoppingCart} label="Orders created"  value={kpis.orders_created}  tone="indigo"  />
              <KPI icon={CheckCircle2} label="Orders fulfilled"value={kpis.orders_fulfilled}tone="teal"    />
              <KPI icon={PackageCheck} label="Net movement"    value={kpis.net_movement}    tone="slate"   />
            </div>
          )}

          {/* Sections */}
          {loading && !summary && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-blue-600" />
              Loading day summary…
            </div>
          )}
          {summary && ACTIVITY_TYPES.filter((t) => selected.has(t.key) && sections[t.key]).map((t) => (
            <SectionCard key={t.key} type={t} data={sections[t.key]} onDeepDivePdf={() => openPdf({ deepDive: t.key })} onDeepDiveXlsx={() => downloadXlsx({ deepDive: t.key })} />
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

function KPI({ icon: Icon, label, value, tone }) {
  const toneClasses = {
    emerald: 'text-emerald-800 bg-emerald-50 border-emerald-200',
    rose:    'text-rose-800 bg-rose-50 border-rose-200',
    blue:    'text-blue-800 bg-blue-50 border-blue-200',
    indigo:  'text-indigo-800 bg-indigo-50 border-indigo-200',
    teal:    'text-teal-800 bg-teal-50 border-teal-200',
    slate:   'text-slate-800 bg-slate-50 border-slate-200',
  }[tone] || 'text-slate-800 bg-slate-50 border-slate-200';
  return (
    <div className={`rounded-xl border p-3 ${toneClasses}`} data-testid={`day-close-kpi-${label.toLowerCase().replace(/\s|\//g, '-')}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function SectionCard({ type, data, onDeepDivePdf, onDeepDiveXlsx }) {
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`day-close-section-${type.key}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <type.icon className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-bold text-slate-900">{type.label}</h3>
          <span className="text-xs text-slate-500">·  {data?.count || 0} row(s)</span>
          {Object.keys(totals).length > 0 && (
            <span className="text-xs text-slate-500 ml-2">
              {Object.entries(totals).map(([k, v]) => `${k}=${v}`).join(' · ')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onDeepDivePdf}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-[#0078D4] bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            data-testid={`day-close-deep-dive-${type.key}`}
          >
            <FileDown className="w-3.5 h-3.5" />
            PDF
          </button>
          <button
            type="button"
            onClick={onDeepDiveXlsx}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100"
            data-testid={`day-close-deep-dive-xlsx-${type.key}`}
            title="Deep-dive Excel export for this activity"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            Excel
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-500">
          No {type.label.toLowerCase()} recorded for this day.
        </div>
      ) : (
        <SectionTable type={type.key} rows={rows} />
      )}
    </div>
  );
}

function SectionTable({ type, rows }) {
  const fmtTs = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toISOString().slice(11, 19); } catch { return String(ts).slice(11, 19); }
  };
  if (type === 'receiving' || type === 'manual_ledger') {
    return (
      <div className="overflow-x-auto"><table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">SKU</th><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-left">Location</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 font-mono text-xs">{fmtTs(r.ts)}</td>
              <td className="px-3 py-1.5 font-mono">{r.sku || '—'}</td>
              <td className="px-3 py-1.5">{r.product_name || '—'}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{r.qty}</td>
              <td className="px-3 py-1.5 text-xs">{r.location_id || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }
  if (type === 'dispatch') {
    return (
      <div className="overflow-x-auto"><table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">SKU</th><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-left">Order</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 font-mono text-xs">{fmtTs(r.ts)}</td>
              <td className="px-3 py-1.5 font-mono">{r.sku || '—'}</td>
              <td className="px-3 py-1.5">{r.product_name || '—'}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{r.qty}</td>
              <td className="px-3 py-1.5 font-mono text-xs" data-testid={`day-close-dispatch-order-${i}`}>
                {(r.order_number || '').trim()
                  || (r.order_id ? `#${r.order_id.slice(0, 8)}` : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }
  if (type === 'adjustments') {
    return (
      <div className="overflow-x-auto"><table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Adj No.</th><th className="px-3 py-2 text-left">Reason</th><th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">IN</th><th className="px-3 py-2 text-right">OUT</th><th className="px-3 py-2 text-left">User</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 font-mono text-xs">{fmtTs(r.ts)}</td>
              <td className="px-3 py-1.5 font-mono font-bold text-[#0078D4]">{r.adjustment_no}</td>
              <td className="px-3 py-1.5">{r.reason_label}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.items_count}</td>
              <td className="px-3 py-1.5 text-right text-emerald-700 font-semibold">+{r.total_qty_in}</td>
              <td className="px-3 py-1.5 text-right text-rose-700 font-semibold">−{r.total_qty_out}</td>
              <td className="px-3 py-1.5 text-xs">{r.user_name}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }
  if (type === 'new_orders' || type === 'fulfilled') {
    return (
      <div className="overflow-x-auto"><table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Order</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">Total</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 font-mono text-xs">{fmtTs(r.ts)}</td>
              <td className="px-3 py-1.5 font-mono">{r.order_number}</td>
              <td className="px-3 py-1.5">{r.customer_name || '—'}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.items_count}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{Number(r.total || 0).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }
  if (type === 'payments') {
    return (
      <div className="overflow-x-auto"><table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Reference</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Method</th><th className="px-3 py-2 text-right">Amount</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 font-mono text-xs">{fmtTs(r.ts)}</td>
              <td className="px-3 py-1.5 font-mono">{r.reference}</td>
              <td className="px-3 py-1.5">{r.customer_name || '—'}</td>
              <td className="px-3 py-1.5">{r.method}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{Number(r.amount || 0).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }
  return null;
}
